import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CapturedPhoto } from "@/components/common/record-capture/captured-photo";

/**
 * Horizontal filmstrip shown below the markup stage when batching. One thumb
 * per captured photo (its `previewUrl`), a 1-based index, and an active ring on
 * the current photo. A trailing dashed "+" tile returns to the camera to
 * capture more. See [[burst-camera.spec]] "Batch + Filmstrip".
 */

export interface MarkupFilmstripProps {
  photos: CapturedPhoto[];
  /** Index of the photo currently open in the editor. */
  currentIndex: number;
  onSelect: (index: number) => void;
  /** Tap the dashed "+" tile → back to the camera for another shot. */
  onAddMore: () => void;
}

export function MarkupFilmstrip({
  photos,
  currentIndex,
  onSelect,
  onAddMore,
}: MarkupFilmstripProps) {
  return (
    <div
      className="flex flex-none items-center gap-2 overflow-x-auto border-t border-white/[0.08] bg-black px-3 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      data-testid="markup-filmstrip"
    >
      {photos.map((photo, i) => {
        const isActive = i === currentIndex;
        return (
          <button
            key={photo.id}
            type="button"
            aria-label={`Photo ${i + 1}`}
            aria-current={isActive}
            onClick={() => onSelect(i)}
            data-testid="filmstrip-thumb"
            className={cn(
              "relative h-[52px] w-[52px] flex-none cursor-pointer overflow-hidden rounded-lg border-2 bg-neutral-800",
              isActive ? "border-primary" : "border-transparent",
            )}
          >
            <img
              src={photo.previewUrl}
              alt=""
              className="h-full w-full object-cover"
            />
            <span className="absolute left-1 top-0.5 font-mono text-[10px] font-bold text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.8)]">
              {i + 1}
            </span>
          </button>
        );
      })}

      <button
        type="button"
        aria-label="Take another photo"
        onClick={onAddMore}
        data-testid="filmstrip-add"
        className="grid h-[52px] w-[52px] flex-none cursor-pointer place-items-center rounded-lg border-[1.5px] border-dashed border-white/[0.34] bg-white/[0.04] text-white/70"
      >
        <Plus className="h-5 w-5" />
      </button>
    </div>
  );
}
